/* ============================================================
   kp-particle.js — Kingpin Canvas Homepage
   Three.js particle field + crown wireframe + GSAP animations
   ============================================================ */
(function () {
  'use strict';

  /* Retry until Three.js + GSAP CDN scripts have loaded */
  function initAll() {
    if (typeof THREE === 'undefined' || typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
      setTimeout(initAll, 60);
      return;
    }
    initParticles();
    initPreloader();
    initGSAP();
  }

  /* ── THREE.JS PARTICLE SYSTEM ── */
  function initParticles() {
    var prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var canvas = document.getElementById('kp-canvas');
    if (prefersReduced || !canvas) return;

    var renderer = new THREE.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);

    var scene = new THREE.Scene();
    var camera = new THREE.PerspectiveCamera(60, window.innerWidth / window.innerHeight, 0.1, 500);
    camera.position.z = 80;

    var isMobile = window.innerWidth < 768;
    var COUNT = isMobile ? 1200 : 3000;

    var geo = new THREE.BufferGeometry();
    var positions = new Float32Array(COUNT * 3);
    var colors    = new Float32Array(COUNT * 3);
    var sizes     = new Float32Array(COUNT);
    var randoms   = new Float32Array(COUNT);

    for (var i = 0; i < COUNT; i++) {
      var r     = 20 + Math.random() * 55;
      var theta = Math.random() * Math.PI * 2;
      var phi   = Math.acos(2 * Math.random() - 1);
      positions[i*3]     = r * Math.sin(phi) * Math.cos(theta);
      positions[i*3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i*3 + 2] = r * Math.cos(phi);

      var bright = 0.65 + Math.random() * 0.35;
      colors[i*3]     = bright;
      colors[i*3 + 1] = bright * (0.55 + Math.random() * 0.3);
      colors[i*3 + 2] = bright * (0.05 + Math.random() * 0.15);

      sizes[i]   = 1.2 + Math.random() * 4.0;
      randoms[i] = Math.random() * Math.PI * 2;
    }

    geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geo.setAttribute('color',    new THREE.BufferAttribute(colors, 3));
    geo.setAttribute('size',     new THREE.BufferAttribute(sizes, 1));
    geo.setAttribute('aRandom',  new THREE.BufferAttribute(randoms, 1));

    var mat = new THREE.ShaderMaterial({
      uniforms: {
        uTime:  { value: 0.0 },
        uMouse: { value: new THREE.Vector2(0.0, 0.0) },
        uBurst: { value: new THREE.Vector4(0.0, 0.0, 0.0, -99.0) }
      },
      vertexShader: [
        'attribute float size;',
        'attribute float aRandom;',
        'varying vec3 vColor;',
        'varying float vGlow;',
        'uniform float uTime;',
        'uniform vec2 uMouse;',
        'uniform vec4 uBurst;',
        'void main() {',
        '  vColor = color;',
        '  vec3 pos = position;',
        '  pos.x += sin(pos.z * 0.016 + uTime * 0.22 + aRandom) * 2.5;',
        '  pos.y += cos(pos.x * 0.011 + uTime * 0.17 + aRandom * 1.3) * 2.5;',
        '  pos.z += sin(pos.y * 0.009 + uTime * 0.13) * 1.5;',
        '  float twinkle = 0.7 + 0.3 * sin(uTime * 2.8 + aRandom * 6.28);',
        '  vec2 mWorld = uMouse * 88.0;',
        '  float mDist = length(mWorld - pos.xy);',
        '  if (mDist < 40.0) {',
        '    vec2 dir = normalize(pos.xy - mWorld);',
        '    float force = (1.0 - mDist / 40.0);',
        '    pos.xy += dir * force * force * 28.0;',
        '  }',
        '  float burstAge = uTime - uBurst.w;',
        '  if (burstAge > 0.0 && burstAge < 1.4) {',
        '    float wavePeak = smoothstep(0.0, 0.5, burstAge) * smoothstep(1.4, 0.5, burstAge);',
        '    vec2 bWorld = uBurst.xy * 88.0;',
        '    float bDist = length(bWorld - pos.xy);',
        '    if (bDist < 80.0) {',
        '      vec2 bDir = length(bWorld - pos.xy) > 0.01',
        '        ? normalize(pos.xy - bWorld)',
        '        : vec2(cos(aRandom), sin(aRandom));',
        '      float decay = 1.0 - burstAge / 1.4;',
        '      pos.xy += bDir * wavePeak * decay * (80.0 - bDist) * 0.45;',
        '    }',
        '    vGlow = wavePeak * 0.8;',
        '  } else {',
        '    vGlow = 0.0;',
        '  }',
        '  vec4 mv = modelViewMatrix * vec4(pos, 1.0);',
        '  gl_PointSize = size * twinkle * (240.0 / -mv.z);',
        '  gl_Position = projectionMatrix * mv;',
        '}'
      ].join('\n'),
      fragmentShader: [
        'varying vec3 vColor;',
        'varying float vGlow;',
        'void main() {',
        '  vec2 uv = gl_PointCoord - 0.5;',
        '  float d = length(uv);',
        '  if (d > 0.5) discard;',
        '  float glow = exp(-d * d * 8.0);',
        '  vec3 col = mix(vColor, vec3(1.0, 0.9, 0.5), vGlow * 0.6);',
        '  gl_FragColor = vec4(col, glow * (0.82 + vGlow * 0.18));',
        '}'
      ].join('\n'),
      transparent:  true,
      blending:     THREE.AdditiveBlending,
      depthWrite:   false,
      vertexColors: true
    });

    var particles = new THREE.Points(geo, mat);
    scene.add(particles);

    /* ── Crown wireframe ── */
    var crownGroup = (function buildCrown() {
      var group = new THREE.Group();
      var lineMat = new THREE.LineBasicMaterial({
        color: 0xC9A84C,
        transparent: true,
        opacity: isMobile ? 0.55 : 0.75
      });

      var baseEdges = new THREE.EdgesGeometry(new THREE.BoxGeometry(18, 0.7, 1.6));
      group.add(new THREE.LineSegments(baseEdges, lineMat));

      var bandGeo = new THREE.TorusGeometry(9, 0.45, 8, 64, Math.PI);
      var band = new THREE.LineSegments(new THREE.EdgesGeometry(bandGeo), lineMat);
      band.rotation.x = Math.PI / 2;
      band.position.y = 0.35;
      group.add(band);

      [{ x: -9, r: 0.9 }, { x: 0, r: 1.1 }, { x: 9, r: 0.9 }].forEach(function(g) {
        var gem = new THREE.LineSegments(
          new THREE.EdgesGeometry(new THREE.TorusGeometry(g.r, 0.15, 6, 16)),
          lineMat
        );
        gem.position.set(g.x, 0.35, 0);
        gem.rotation.x = Math.PI / 2;
        group.add(gem);
      });

      [
        { x: -8.8, h: 5.8, r: 0.85 },
        { x: -4.4, h: 3.8, r: 0.65 },
        { x:  0.0, h: 8.0, r: 1.05 },
        { x:  4.4, h: 3.8, r: 0.65 },
        { x:  8.8, h: 5.8, r: 0.85 }
      ].forEach(function(s) {
        var cone = new THREE.LineSegments(
          new THREE.EdgesGeometry(new THREE.ConeGeometry(s.r, s.h, 8)),
          lineMat
        );
        cone.position.set(s.x, s.h * 0.5 + 0.35, 0);
        group.add(cone);
      });

      var diamond = new THREE.LineSegments(
        new THREE.EdgesGeometry(new THREE.OctahedronGeometry(1.1)),
        lineMat
      );
      diamond.position.set(0, 8.35 + 1.1, 0);
      group.add(diamond);

      group.scale.setScalar(isMobile ? 1.2 : 2.4);
      return group;
    })();

    var crownGlow = crownGroup.clone();
    crownGlow.scale.multiplyScalar(1.06);
    crownGlow.traverse(function(child) {
      if (child.material) {
        child.material = child.material.clone();
        child.material.opacity = isMobile ? 0.18 : 0.22;
      }
    });

    scene.add(crownGlow);
    scene.add(crownGroup);

    var crownLineMats = [];
    crownGroup.traverse(function(c) { if (c.material) crownLineMats.push(c.material); });

    var crownRotateEnd = 0.28;
    var crownRotateFraction = 0;

    function calibrateCrown() {
      var pillarsEl = document.querySelector('.kp-pillars-canvas');
      if (!pillarsEl) return;
      var docH = document.documentElement.scrollHeight - window.innerHeight;
      if (docH <= 0) return;
      crownRotateEnd = Math.max(0.05, (pillarsEl.offsetTop - window.innerHeight * 1.2) / docH);
    }
    if (document.readyState === 'complete') { calibrateCrown(); }
    else { window.addEventListener('load', calibrateCrown, { once: true }); }

    var targetCamZ = 80;
    var scrollProgress = 0;

    window.addEventListener('scroll', function () {
      var docH = document.documentElement.scrollHeight - window.innerHeight;
      scrollProgress = docH > 0 ? Math.min(1, window.scrollY / docH) : 0;
      targetCamZ = 80 - scrollProgress * 65;
      particles.rotation.y = scrollProgress * Math.PI * 0.5;
      crownRotateFraction = crownRotateEnd > 0
        ? Math.min(1.0, scrollProgress / crownRotateEnd)
        : 0;
    }, { passive: true });

    var mouseVec = new THREE.Vector2(0, 0);
    window.addEventListener('mousemove', function (e) {
      mouseVec.x =  (e.clientX / window.innerWidth)  * 2 - 1;
      mouseVec.y = -((e.clientY / window.innerHeight) * 2 - 1);
    }, { passive: true });

    window.addEventListener('pointerdown', function (e) {
      var bx =  (e.clientX / window.innerWidth)  * 2 - 1;
      var by = -((e.clientY / window.innerHeight) * 2 - 1);
      mat.uniforms.uBurst.value.set(bx, by, 80.0, mat.uniforms.uTime.value);
    }, { passive: true });

    window.addEventListener('resize', function () {
      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
    });

    var clock = new THREE.Clock();

    function animate() {
      if (document.hidden) { requestAnimationFrame(animate); return; }
      requestAnimationFrame(animate);
      var t = clock.getElapsedTime();

      mat.uniforms.uTime.value = t;
      mat.uniforms.uMouse.value.set(mouseVec.x, mouseVec.y);
      camera.position.z += (targetCamZ - camera.position.z) * 0.04;
      particles.rotation.x = t * 0.012;

      var cf = crownRotateFraction;
      var easedCf = cf * cf * (3.0 - 2.0 * cf);
      crownGroup.rotation.y = easedCf * Math.PI * 4;
      crownGroup.rotation.x = Math.sin(t * 0.35) * 0.12;
      crownGroup.rotation.z = Math.cos(t * 0.28) * 0.06;
      crownGroup.position.y = Math.sin(t * 0.55) * 1.2;
      crownGroup.position.z = -2 + Math.min(scrollProgress, crownRotateEnd) * 4;

      var activeOp = isMobile ? 0.55 : 0.75;
      var quietOp  = 0.07;
      var pastStop = crownRotateEnd < 1.0
        ? Math.min(1.0, Math.max(0, (scrollProgress - crownRotateEnd) / Math.max(0.01, (1.0 - crownRotateEnd) * 0.35)))
        : 0;
      var targetOp = activeOp + (quietOp - activeOp) * pastStop;
      crownLineMats.forEach(function(m) { m.opacity += (targetOp - m.opacity) * 0.06; });

      crownGlow.rotation.copy(crownGroup.rotation);
      crownGlow.position.copy(crownGroup.position);
      crownGlow.scale.copy(crownGroup.scale).multiplyScalar(1.06);

      renderer.render(scene, camera);
    }
    animate();
  }

  /* ── PRELOADER ── */
  function initPreloader() {
    var preloader = document.getElementById('kp-preloader');
    var countEl   = document.getElementById('kp-preloader-count');
    if (!preloader || !countEl) return;

    document.body.style.overflow = 'hidden';
    var start = null;
    var duration = 1800;

    function tick(ts) {
      if (!start) start = ts;
      var pct = Math.min(100, Math.floor(((ts - start) / duration) * 100));
      countEl.textContent = (pct < 10 ? '0' : '') + pct;
      if (pct < 100) {
        requestAnimationFrame(tick);
      } else {
        preloader.classList.add('is-done');
        document.body.style.overflow = '';
        preloader.addEventListener('transitionend', function () {
          preloader.style.display = 'none';
        }, { once: true });
      }
    }
    requestAnimationFrame(tick);
  }

  /* ── GSAP SCROLL ANIMATIONS ── */
  function initGSAP() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.querySelectorAll('.kp-hero-eyebrow, .kp-hero-sub, .kp-hero-cta, .kp-hero-scroll-hint').forEach(function (el) {
        el.style.opacity = '1';
      });
      document.querySelectorAll('.kp-word-inner').forEach(function (el) {
        el.style.transform = 'translateY(0)';
      });
      document.querySelectorAll('.kp-stmt-text .kp-word').forEach(function (el) {
        el.style.opacity = '1';
      });
      document.querySelectorAll('.kp-pillar-card, .kp-imagery-slot, .kp-product-card').forEach(function (el) {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    gsap.registerPlugin(ScrollTrigger);

    var heroTL = gsap.timeline({ delay: 1.9 });
    heroTL
      .from('.kp-hero-eyebrow', { opacity: 0, y: 12, duration: 0.6, ease: 'power2.out' })
      .to('.kp-hero-headline .kp-word-inner', {
        y: 0,
        duration: 1,
        stagger: 0.12,
        ease: 'power4.out'
      }, '-=0.3')
      .from('.kp-hero-sub', { opacity: 0, y: 16, duration: 0.7, ease: 'power2.out' }, '-=0.5')
      .from('.kp-hero-cta', { opacity: 0, y: 12, duration: 0.5, ease: 'power2.out' }, '-=0.4')
      .from('.kp-hero-scroll-hint', { opacity: 0, duration: 0.5 }, '-=0.2');

    var words = document.querySelectorAll('.kp-stmt-text .kp-word');
    var stmtTL = gsap.timeline({
      scrollTrigger: {
        trigger: '.kp-brand-statement',
        start: 'top top',
        end: '+=180%',
        pin: true,
        anticipatePin: 1,
        scrub: 0.8
      }
    });
    words.forEach(function (word, i) {
      stmtTL.to(word, { opacity: 1, duration: 0.3 }, i * 0.22);
    });

    gsap.to('.kp-pillar-card', {
      opacity: 1, y: 0, duration: 0.7, stagger: 0.15, ease: 'power3.out',
      scrollTrigger: { trigger: '.kp-pillars-grid', start: 'top 80%' }
    });

    gsap.to('.kp-imagery-slot', {
      opacity: 1, scale: 1, duration: 0.8, stagger: 0.12, ease: 'power2.out',
      scrollTrigger: { trigger: '.kp-imagery-grid', start: 'top 75%' }
    });

    gsap.to('.kp-product-card', {
      opacity: 1, y: 0, duration: 0.7, stagger: 0.1, ease: 'power3.out',
      scrollTrigger: { trigger: '.kp-drops-grid', start: 'top 80%' }
    });

    gsap.from('.kp-editorial-content', {
      opacity: 0, y: 30, duration: 0.9, ease: 'power2.out',
      scrollTrigger: { trigger: '.kp-editorial-section', start: 'top 70%' }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

})();
